import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { formatPrice } from "@/components/product-step/types";
import { useMarket } from "@/hooks/useMarket";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useBookingStore } from "@/stores/bookingStore";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { CADENCE_CONTENTS_KEY } from "@/utils/membershipPricing";
import { useBundleSummary } from "@/hooks/useBundleSummary";
import { useBloodCheckout } from "@/hooks/useBloodCheckout";
import {
  bundleSavingsCents,
  bundleSeparateTotalCents,
} from "@/utils/bloodMembershipPricing";
import { CadencePickerModal } from "@/components/booking-form/CadencePickerModal";

interface MemberPaymentSummaryProps {
  language: BookingLanguage;
}

/**
 * The details step's payment section for someone who already chose to book as
 * a member — "Book as a member" on a scan card attaches the membership up
 * front, so by the time they reach this step there is nothing left to pick.
 *
 * Until now that meant the whole payment section was hidden (FormFields'
 * `showPaymentBlock` excluded `membershipPlanActive`), and the user reached the
 * last step with a subscription in the cart and no statement of what they were
 * paying for. This says it: one card, no radio, no one-off alternative.
 *
 * Numbers come from useBundleSummary — the same source the order summary uses,
 * so the two can't drift.
 */
export const MemberPaymentSummary: React.FC<MemberPaymentSummaryProps> = ({
  language,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { market } = useMarket();
  // Getters, not slices: the store keeps ids and resolves them against the
  // product list, so there is no `selectedProductDetails` field to subscribe
  // to. Re-read on every render, which is what the order summary does too.
  const selectedProductDetails = useBookingFlowStore((s) =>
    s.getSelectedProductDetails(),
  );
  const selectedAddOnDetails = useBookingFlowStore((s) =>
    s.getSelectedAddOnDetails(),
  );
  const cadencePlan = useMembershipPlanStore((s) => s.plan);
  const [cadenceModalOpen, setCadenceModalOpen] = useState(false);
  const discount = useBookingStore((s) => s.discount);

  const { bundlePricing } = useBundleSummary(
    selectedProductDetails,
    selectedAddOnDetails,
  );
  // The blood model prices the scan-less membership cart (base test at the
  // cadence + member-priced add-ons). Returning null here for those carts —
  // as this card originally did — rendered the "Your payment" section as an
  // empty frame the moment the routing widened to membership-first carts.
  const { pricing: bloodPricing } = useBloodCheckout();

  const cart = [...selectedProductDetails, ...selectedAddOnDetails];
  // The struck price is what this basket costs booked separately, follow-ups
  // included — the same base the saving is measured from, so the two agree.
  // Blood carts strike their standalone total instead; useBloodCheckout
  // already clamps its savings to 0 for cadences that add follow-ups, which
  // hides both the strike and the badge exactly like the chooser card does.
  const separateTotalCents = bundlePricing
    ? bundleSeparateTotalCents(cart, cadencePlan)
    : bloodPricing.fullTotalCents;
  const savingsCents = bundlePricing
    ? bundleSavingsCents(cart, bundlePricing.payNowCents, cadencePlan)
    : bloodPricing.savingsCents;
  // An applied invitation code nets the pay-now amount, exactly as the order
  // summary nets its total — this card used to keep showing the pre-voucher
  // figure ("CHF 3'409 charged today" beside a sidebar totalling 3'259).
  // Over the invoice LINES: Stripe rounds a percentage coupon per line.
  const payNowCents = bundlePricing
    ? bundlePricing.payNowCents
    : bloodPricing.memberTotalCents;
  const payNowAfterVoucher =
    payNowCents -
    voucherDiscountCents(
      discount,
      bundlePricing ? bundlePricing.lines : bloodPricing.memberLines,
    );

  return (
    <>
      {/* Full width, and the only card here: the alternative (pay once, no
          membership) was already declined on the packages page. Green border
          rather than a radio — it states a choice, it doesn't offer one. */}
      <div className="rounded-xl border-2 border-deep-green-600 bg-deep-green-100/40 px-5 py-5">
        <span className="font-medium text-gray-900 text-lg">
          {t("paymentInfo.bookingAsMember")}
        </span>
        <p className="text-sm text-gray-500 mt-2">
          {t("paymentInfo.memberNote")}
        </p>

        {/* What the membership actually contains at the chosen cadence, and
            the way back to that choice — otherwise the cadence is only
            changeable by walking the flow again. */}
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-deep-blue-700">
            {t(CADENCE_CONTENTS_KEY[cadencePlan])}
          </span>
          <button
            type="button"
            onClick={() => setCadenceModalOpen(true)}
            className="text-sm font-normal normal-case text-gray-500 underline"
          >
            {t("paymentInfo.changeCadence")}
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {savingsCents > 0 && (
            <span className="text-xs text-gray-400 line-through">
              {formatPrice(separateTotalCents, market)}
            </span>
          )}
          <span
            data-testid="member-summary-price"
            className="text-xl font-bold text-gray-900"
          >
            {formatPrice(payNowAfterVoucher, market)}
          </span>
          {/* A statement, not a call to action: the choice was already made on
              the packages page, so "Save CHF 200" would be asking for
              something the user has done.
              Green, not the light-blue the payment-step card uses: that badge
              sits on white there, but this card is deep-green-100/40 and blue
              on green read as a clash. */}
          {savingsCents > 0 && (
            <span className="rounded-full bg-deep-green-200 text-deep-green-800 text-xs font-medium px-2.5 py-0.5">
              {t("paymentInfo.youAreSaving", {
                amount: formatPrice(savingsCents, market),
              })}
            </span>
          )}
        </div>

        {/* One charge now, and what happens a year later. Same wording the
            order summary uses on the recurring line, so the two agree. */}
        <p className="mt-3 text-sm text-gray-700">
          {t("paymentInfo.oneChargeToday", {
            amount: formatPrice(payNowAfterVoucher, market),
          })}{" "}
          {t("orderSummary.membershipChargedToday")}
        </p>
      </div>

      <CadencePickerModal
        isOpen={cadenceModalOpen}
        onClose={() => setCadenceModalOpen(false)}
        language={language}
        source="payment_step"
      />
    </>
  );
};

export default MemberPaymentSummary;
